/**
 * Terrain rules for Memoir '44.
 *
 * Covers both base TerrainTypes and OverlayTypes. When a hex has both a base
 * terrain and an overlay, call getCombinedTerrainEffect() — it merges the two
 * rule sets following the "best protection wins, not cumulative" rule:
 *   - defenseBonus per unit type: max(base, overlay)
 *   - attackPenalty per unit type: max(base, overlay)
 *   - ignoreFlags: max(base, overlay)
 * All boolean flags (stopsMovement, blocksBattleAfterMove, etc.) use OR.
 */

import type { UnitType } from './types';

export interface TerrainEffect {
  name: string;
  description: string;

  // ── Movement ─────────────────────────────────────────────────────────
  stopsMovement: boolean;
  moveExempt: UnitType[];
  impassableTo: UnitType[];
  /** Max hexes a unit may move the turn it leaves this hex (e.g. Hedgerow, Marsh = 1). */
  leavingMoveLimit?: number;

  // ── Combat ───────────────────────────────────────────────────────────
  defenseBonus: Record<UnitType, number>;
  attackPenalty: Record<UnitType, number>;
  ignoreFlags: number;
  /** Restricts which unit types benefit from ignoreFlags. Undefined = applies to all types. */
  ignoreFlagsTypes?: UnitType[];
  blocksBattleAfterMove: boolean;
  /** Restricts which unit types blocksBattleAfterMove applies to. Undefined = applies to all types. */
  blocksBattleAfterMoveTypes?: UnitType[];
  /** Unit types that may not battle the turn they moved OUT of this hex (e.g. Marsh — armor only). */
  blocksBattleAfterLeavingTypes?: UnitType[];

  // ── Line of sight ────────────────────────────────────────────────────
  blocksLoS: boolean;

  // ── Special movement ─────────────────────────────────────────────────
  maxMoveDistIfEntering?: number;
  pinnedTypes?: UnitType[];
  impassableForRetreat?: boolean;
  /** Rolling this many dice on entry; each Star rolled costs the entering unit 1 figure. */
  attritionOnEntry?: { dice: number };

  // ── Special combat ───────────────────────────────────────────────────
  /** Unit types that may not battle while standing on this hex. */
  noBattleFromHexTypes?: UnitType[];
  battleAfterMoveOk?: UnitType[];

  // ── Removal ──────────────────────────────────────────────────────────
  removable: boolean;
  removeBy?: {
    infantryAction?: boolean;
    armorCrushes?: boolean;
    unitLeaves?: boolean;
    unitEliminated?: boolean;
    armorAttacks?: boolean;   // armor attacking FROM this overlay crushes/removes it
  };
}

const ZERO: Record<UnitType, number> = { infantry: 0, armor: 0, artillery: 0 };
const ALL: UnitType[] = ['infantry', 'armor', 'artillery'];

export const TERRAIN_RULES: Record<string, TerrainEffect> = {

  clear: {
    name: 'Clear', description: 'Open ground — no special effects.',
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  forest: {
    name: 'Forest',
    description: 'Unit must stop on entry. Cannot battle after moving in. Infantry –1 / Armor –2 attack dice. Blocks LoS.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  hill: {
    name: 'Hill',
    description: 'No movement restriction. Infantry and Armor –1 die when attacking from lower ground. Plateau: no penalty when attacker is also on a hill. Blocks LoS except between units both on hills.',
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 1, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: true, removable: false,
  },

  town: {
    name: 'Town',
    description: 'Unit must stop on entry. Cannot battle after moving in. Infantry –1 / Armor –2 attack dice. Armor in town –2 attack dice. Blocks LoS.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: { infantry: 0, armor: 2, artillery: 0 },
    ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  river: {
    name: 'River',
    description: 'Impassable. Units must use bridges to cross.',
    stopsMovement: true, moveExempt: [], impassableTo: ALL,
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  bridge: {
    name: 'Bridge',
    description: 'Crossing point over rivers.',
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  beach: {
    name: 'Beach',
    description: 'A unit moving onto Beach may only move 2 hexes total. No combat restrictions.',
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    maxMoveDistIfEntering: 2,
    blocksLoS: false, removable: false,
  },

  ocean: {
    name: 'Ocean',
    description: 'Landing zone. Max 1 hex move. Units on Ocean may not battle. Impassable for retreat.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    noBattleFromHexTypes: ALL, impassableForRetreat: true, maxMoveDistIfEntering: 1,
    blocksLoS: false, removable: false,
  },

  hedgerow: {
    name: 'Hedgerow',
    description: 'Must be adjacent to enter. All units stop on entry. Unit leaving may only move 1 hex. Infantry attacker –1 die, Armor attacker –2 dice. Cannot battle after moving in. Blocks LoS.',
    stopsMovement: true, moveExempt: [], impassableTo: ['artillery'],
    leavingMoveLimit: 1,
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  bluff: {
    name: 'Bluff',
    description: [
      'Steep cliff face.',
      'From inland: no movement restriction; infantry and armor –1 die when attacking from lower ground.',
      'From beach direction: infantry may enter but must stop (full move used); armor may not enter at all.',
      'Blocks line of sight (same as hill).',
    ].join(' '),
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 1, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: true, removable: false,
  },

  'impassable-hill': {
    name: 'Impassable Hill',
    description: 'Cannot be entered by any unit (scenario special rule).',
    stopsMovement: true, moveExempt: [], impassableTo: ALL,
    defenseBonus: { infantry: 1, armor: 1, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 1,
    blocksBattleAfterMove: false,
    blocksLoS: true, removable: false,
  },

  pond: {
    name: 'Pond (Impassable)',
    description: 'Cannot be entered by any unit.',
    stopsMovement: true, moveExempt: [], impassableTo: ALL,
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  'frozen-lake': {
    name: 'Frozen Lake',
    description: 'Units may move or retreat across freely — the ice does not stop movement. No battle or combat penalty. Each hex of ice entered risks the unit falling through: roll 2 dice, lose 1 figure per Star rolled.',
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
    attritionOnEntry: { dice: 2 },
  },

  ravine: {
    name: 'Ravine',
    description: 'Impassable to Armor and Artillery; no restriction for Infantry. No combat restrictions. Infantry on a Ravine may ignore the first flag rolled against it. Does not block LoS.',
    stopsMovement: false, moveExempt: [], impassableTo: ['armor', 'artillery'],
    defenseBonus: ZERO, attackPenalty: ZERO,
    ignoreFlags: 1,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  trench: {
    name: 'Trench',
    description: [
      'Infantry and Armor must stop on entry. Impassable to Artillery.',
      'Armor on a Trench may not battle.',
      'Attacking a unit in Trenches: Infantry and Armor –1 die, Artillery unaffected.',
      'Infantry in Trenches may ignore the first flag rolled against it.',
      'Does not block line of sight.',
    ].join(' '),
    stopsMovement: true, moveExempt: [], impassableTo: ['artillery'],
    defenseBonus: { infantry: 1, armor: 1, artillery: 0 }, attackPenalty: ZERO,
    ignoreFlags: 1, ignoreFlagsTypes: ['infantry'],
    blocksBattleAfterMove: false,
    noBattleFromHexTypes: ['armor'],
    blocksLoS: false, removable: false,
  },

  'city-ruins': {
    name: 'City Ruins',
    description: [
      'By default, plays exactly like Town & Village: unit must stop on entry, cannot battle after moving in,',
      'Infantry –1 / Armor –2 attack dice, Armor attacking from here –2 dice, blocks LoS.',
      'Only Infantry may enter. A unit on City Ruins may ignore the first flag rolled against it.',
    ].join(' '),
    stopsMovement: true, moveExempt: [], impassableTo: ['armor', 'artillery'],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: { infantry: 0, armor: 2, artillery: 0 },
    ignoreFlags: 1,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  'hill-forest': {
    name: 'Hill with Forest',
    description: 'Visual variant of a forested hill; plays exactly like Forest (M44 p.13) — unit must stop on entry, cannot battle after moving in, Infantry –1 / Armor –2 attack dice. Blocks LoS.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  'hill-village': {
    name: 'Hill with Village',
    description: 'Visual variant of a hilltop village; plays exactly like Town & Village (M44 p.14) — unit must stop on entry, cannot battle after moving in, Infantry –1 / Armor –2 attack dice, Armor attacking from here –2 dice. Blocks LoS.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: { infantry: 0, armor: 2, artillery: 0 },
    ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  'factory-complex': {
    name: 'Factory Complex',
    description: 'By default, plays exactly like Town & Village (M44 p.14) — see scenario-specific rules for overrides.',
    stopsMovement: true, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: { infantry: 0, armor: 2, artillery: 0 },
    ignoreFlags: 0,
    blocksBattleAfterMove: true,
    blocksLoS: true, removable: false,
  },

  marsh: {
    name: 'Marsh',
    description: [
      'Infantry or Armor that moves onto a Marsh hex must stop and may not move further that turn.',
      'A unit that leaves a Marsh hex may only move onto an adjacent hex. Impassable to Artillery.',
      'Infantry has no battle restrictions. Armor may not battle the turn it moves onto or out of a Marsh hex.',
      'Armor that makes a successful Close Assault into Marsh may Take Ground but may not Armor Overrun.',
      'Does not block line of sight.',
    ].join(' '),
    stopsMovement: true, moveExempt: [], impassableTo: ['artillery'],
    leavingMoveLimit: 1,
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 0,
    blocksBattleAfterMove: true, blocksBattleAfterMoveTypes: ['armor'],
    blocksBattleAfterLeavingTypes: ['armor'],
    blocksLoS: false, removable: false,
  },

  // ── Overlay types ─────────────────────────────────────────────────────────

  'barbed-wire': {
    name: 'Barbed Wire',
    description: [
      'All units stop when entering.',
      'Infantry attacks –1 die when attacking from wire.',
      'Infantry may remove wire instead of battling.',
      'Armor automatically crushes wire when moving onto the hex.',
    ].join(' '),
    stopsMovement: true,
    moveExempt: ['armor'],
    impassableTo: [],
    defenseBonus: ZERO,
    attackPenalty: { infantry: 1, armor: 0, artillery: 0 },
    ignoreFlags: 0,
    blocksBattleAfterMove: false,
    blocksLoS: false,
    removable: true,
    removeBy: {
      infantryAction: true,
      armorCrushes: true,
      armorAttacks: true,
    },
  },

  hedgehog: {
    name: 'Hedgehog',
    description: 'Infantry only. No movement restriction. Ignore the first Flag rolled against the occupying unit.',
    stopsMovement: false, moveExempt: [], impassableTo: ['armor', 'artillery'],
    defenseBonus: ZERO, attackPenalty: ZERO, ignoreFlags: 1,
    blocksBattleAfterMove: false,
    blocksLoS: false, removable: false,
  },

  bunker: {
    name: 'Bunker',
    description: [
      'Only infantry may enter (armor/artillery impassable).',
      'Infantry may battle the turn they move onto a Bunker.',
      'Artillery that starts on a Bunker may not move.',
      'Attacker: Infantry –1 die, Armor –2 dice, Artillery unchanged.',
      'Ignore the first Flag (designated side only).',
      'Blocks line of sight.',
    ].join(' '),
    stopsMovement: true, moveExempt: [], impassableTo: ['armor', 'artillery'],
    defenseBonus: { infantry: 1, armor: 2, artillery: 0 },
    attackPenalty: ZERO, ignoreFlags: 1,
    blocksBattleAfterMove: false,
    battleAfterMoveOk: ['infantry'],
    pinnedTypes: ['artillery'],
    blocksLoS: true, removable: false,
  },

  sandbags: {
    name: 'Sandbags',
    description: [
      'Attacker rolls –1 die vs infantry and armor.',
      'Unit in sandbags may ignore 1 flag.',
      'Sandbags are removed when the occupying unit moves away or is eliminated.',
    ].join(' '),
    stopsMovement: false, moveExempt: [], impassableTo: [],
    defenseBonus: { infantry: 1, armor: 1, artillery: 0 },
    attackPenalty: ZERO,
    ignoreFlags: 1,
    blocksBattleAfterMove: false,
    blocksLoS: false,
    removable: true,
    removeBy: {
      unitLeaves: true,
      unitEliminated: true,
    },
  },

};

/** Look up a rule, falling back to 'clear' for unknown types. */
export function getTerrainRule(terrainType: string): TerrainEffect {
  return TERRAIN_RULES[terrainType] ?? TERRAIN_RULES.clear;
}

/**
 * Combine a base terrain effect with an optional overlay effect.
 *
 * Defense dice bonuses follow the "best protection wins" rule — only the
 * highest single value applies per attacker type, not the sum.
 * Boolean flags (blocksLoS, stopsMovement, …) use OR (either triggers = both trigger).
 * impassableTo uses union; moveExempt uses intersection (must be exempt from both).
 */
export function getCombinedTerrainEffect(baseType: string, overlayType?: string): TerrainEffect {
  const base = getTerrainRule(baseType);
  if (!overlayType) return base;
  const ovl = TERRAIN_RULES[overlayType];
  if (!ovl) return base;

  const unitTypes: UnitType[] = ['infantry', 'armor', 'artillery'];

  const defenseBonus = Object.fromEntries(
    unitTypes.map(t => [t, Math.max(base.defenseBonus[t], ovl.defenseBonus[t])]),
  ) as Record<UnitType, number>;

  const attackPenalty = Object.fromEntries(
    unitTypes.map(t => [t, Math.max(base.attackPenalty[t], ovl.attackPenalty[t])]),
  ) as Record<UnitType, number>;

  const impassableTo = [...new Set([...base.impassableTo, ...ovl.impassableTo])];
  const moveExempt   = base.moveExempt.filter(t => ovl.moveExempt.includes(t));

  const maxMoveDist = minDefined(base.maxMoveDistIfEntering, ovl.maxMoveDistIfEntering);
  const pinnedTypes = unionOrUndef(base.pinnedTypes, ovl.pinnedTypes);
  const battleAfterMoveOk = unionOrUndef(base.battleAfterMoveOk, ovl.battleAfterMoveOk);
  const noBattleFromHexTypes = unionOrUndef(base.noBattleFromHexTypes, ovl.noBattleFromHexTypes);
  const blocksBattleAfterLeavingTypes = unionOrUndef(base.blocksBattleAfterLeavingTypes, ovl.blocksBattleAfterLeavingTypes);
  const leavingMoveLimit = minDefined(base.leavingMoveLimit, ovl.leavingMoveLimit);
  // Whichever side contributes the winning (max) ignoreFlags value determines the type-scoping.
  const ignoreFlagsTypes = (ovl.ignoreFlags >= base.ignoreFlags ? ovl : base).ignoreFlagsTypes;
  // blocksBattleAfterMove uses OR; the type-scoping follows whichever side(s) actually block.
  const blocksBattleAfterMoveTypes =
    base.blocksBattleAfterMove && ovl.blocksBattleAfterMove
      ? unionOrUndef(base.blocksBattleAfterMoveTypes, ovl.blocksBattleAfterMoveTypes)
      : base.blocksBattleAfterMove ? base.blocksBattleAfterMoveTypes
      : ovl.blocksBattleAfterMove  ? ovl.blocksBattleAfterMoveTypes
      : undefined;

  return {
    name: ovl.name,
    description: `${base.description} ${ovl.description}`,
    stopsMovement: base.stopsMovement || ovl.stopsMovement,
    moveExempt,
    impassableTo,
    leavingMoveLimit,
    defenseBonus,
    attackPenalty,
    ignoreFlags: Math.max(base.ignoreFlags, ovl.ignoreFlags),
    ignoreFlagsTypes,
    blocksBattleAfterMove: base.blocksBattleAfterMove || ovl.blocksBattleAfterMove,
    blocksBattleAfterMoveTypes,
    blocksBattleAfterLeavingTypes,
    blocksLoS: base.blocksLoS || ovl.blocksLoS,
    maxMoveDistIfEntering: maxMoveDist,
    pinnedTypes,
    impassableForRetreat: (base.impassableForRetreat ?? false) || (ovl.impassableForRetreat ?? false) || undefined,
    noBattleFromHexTypes,
    battleAfterMoveOk,
    removable: base.removable || ovl.removable,
    removeBy: ovl.removable ? ovl.removeBy : base.removeBy,
  };
}

function minDefined(a?: number, b?: number): number | undefined {
  if (a === undefined && b === undefined) return undefined;
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

function unionOrUndef<T>(a?: T[], b?: T[]): T[] | undefined {
  if (!a && !b) return undefined;
  return [...new Set([...(a ?? []), ...(b ?? [])])];
}
